import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Mail, ShoppingCart } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';
import CartDrawer from './CartDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [products, setProducts] = useState([]);
  const [extensionCategories, setExtensionCategories] = useState([]);
  const { cartItems, cartCount, setIsCartOpen } = useCart();
  const location = useLocation();
  const { logoUrl } = useSiteSettings();
  const { cart_enabled, pawn_checkout, events_enabled } = useSiteFeatureFlags();

  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Load products for the Products mega menu (grouped by category)
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/store/products`);
        setProducts(Array.isArray(res.data) ? res.data : (res.data.products || []));
      } catch (error) {
        setProducts([]);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    // Live category taxonomy so admin enable/disable instantly reflects in nav
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/store/categories`);
        const topLevel = (Array.isArray(res.data) ? res.data : [])
          .filter((cat) => cat.is_enabled !== false && !cat.parent_id && cat.show_in_menu === true)
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map((cat) => ({
            label: (cat.name || '').toUpperCase(),
            name: cat.name,
            slug: cat.seo_url || (cat.name || '').toLowerCase(),
          }));
        setExtensionCategories(topLevel);
      } catch (error) {
        setExtensionCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const forceDarkHeader = location.pathname === '/shop'
    || location.pathname.startsWith('/shop')
    || location.pathname === '/categories'
    || location.pathname.startsWith('/account')
    || location.pathname.startsWith('/support');

  // Products with dedicated landing pages get their own routes
  const DEDICATED_PAGES = {
    'pkg_aijoomlasecurity': '/products/ai-joomla-security-suite',
    'pkg_vertical_menu': '/products/ai-joomla-mobile-menu',
    'pkg_aijoomlaaccessibility': '/products/ai-joomla-accessibility-widget',
  };

  // Products intentionally excluded from the header "Products" mega menu (still purchasable via /shop)
  const HIDDEN_FROM_PRODUCTS_MENU = ['pkg_myforms_demo'];

  const productHref = (p) => {
    if (DEDICATED_PAGES[p.extension_element]) return DEDICATED_PAGES[p.extension_element];
    return p.seo_url ? `/extensions/${p.seo_url}` : `/product/${p.id}`;
  };
  const productInCategory = (p, categoryName) => {
    const cats = Array.isArray(p.categories) && p.categories.length > 0
      ? p.categories
      : [p.category].filter(Boolean);
    return cats.some((c) => (c || '').toLowerCase() === categoryName.toLowerCase());
  };

  // Top-level category links (shop-to-buy) - synced live from admin categories
  const categoryNav = extensionCategories.map((cat) => ({
    label: cat.label,
    href: `/shop/products?category=${cat.slug}`,
  }));

  // Products mega menu: one column per category, listing that category's product
  // landing/info pages. As new products/landing pages go live in a category,
  // they automatically appear here (no static list to keep updating).
  const productsMegaMenu = {
    label: 'PRODUCTS',
    href: '/shop',
    twoColumn: true,
    megaWide: true,
    columns: extensionCategories.map((cat) => ({
      title: cat.name,
      href: `/shop/products?category=${cat.slug}`,
      items: products
        .filter((p) => productInCategory(p, cat.name) && !HIDDEN_FROM_PRODUCTS_MENU.includes(p.extension_element))
        .slice(0, 8)
        .map((p) => ({ label: p.name, href: productHref(p) })),
    })),
  };

  const navigation = [
    productsMegaMenu,
    ...categoryNav,
    { label: 'CUSTOM', href: '/custom-extensions' },
    { label: 'SECURITY', href: '/products/ai-joomla-security-suite', highlight: true },
  ];


  const handleDropdownToggle = (label) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || forceDarkHeader
            ? 'bg-bots-dark/95 backdrop-blur-md shadow-lg' 
            : 'bg-transparent'
        }`}
        data-testid="main-header"
      >
        {/* Top Bar - hidden on mobile to reduce clutter */}
        <div className="hidden md:block bg-bots-surface/80 border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center text-sm">
            <a
              href="mailto:info@aijoomla.org"
              className="flex items-center text-white hover:text-blue-400 transition-colors"
              data-testid="header-email"
            >
              <Mail className="w-4 h-4 mr-2" />
              info@aijoomla.org
            </a>
            <div className="flex items-center space-x-4">
              {cart_enabled && (
                <>
                  <Link
                    to="/shop"
                    className="text-gray-300 hover:text-white transition-colors"
                    data-testid="header-shop-link"
                  >
                    Shop
                  </Link>
                  <span className="text-gray-600">|</span>
                </>
              )}
              <Link
                to="/contact"
                className="text-gray-300 hover:text-white transition-colors"
                data-testid="header-support-link"
              >
                Support
              </Link>
              <span className="text-gray-600">|</span>
              <Link
                to="/login"
                className="text-gray-300 hover:text-white transition-colors"
                data-testid="header-login-link"
              >
                Login
              </Link>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="header-logo">
              {logoUrl ? (
                <img src={logoUrl} alt="AI Joomla" className="h-10 md:h-12" />
              ) : (
                <span className="text-2xl font-bold text-white">123 Bots</span>
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                <div key={item.label} className="relative group">
                  {(item.items || item.columns) ? (
                    <>
                      {item.href ? (
                        <Link
                          to={item.href}
                          className="flex items-center px-4 py-2 text-white hover:text-blue-400 transition-colors font-medium"
                          data-testid={`nav-${item.label.toLowerCase()}`}
                        >
                          {item.label}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Link>
                      ) : (
                        <button
                          className="flex items-center px-4 py-2 text-white hover:text-blue-400 transition-colors font-medium"
                          onClick={() => handleDropdownToggle(item.label)}
                          data-testid={`nav-${item.label.toLowerCase()}`}
                        >
                          {item.label}
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </button>
                      )}
                      {/* Dropdown */}
                      {item.twoColumn ? (
                        <div className={`absolute top-full left-0 ${item.megaWide ? 'w-[760px]' : 'w-[500px]'} bg-bots-surface border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 mt-1 p-4`}>
                          <div className={`grid ${item.megaWide ? 'grid-cols-4' : 'grid-cols-2'} gap-6`}>
                            {item.columns.map((column, idx) => (
                              <div key={idx}>
                                {column.href ? (
                                  <Link
                                    to={column.href}
                                    className="block text-blue-400 font-bold text-sm mb-3 pb-2 border-b border-gray-700 whitespace-nowrap hover:text-blue-300"
                                  >
                                    {column.title}
                                  </Link>
                                ) : (
                                  <h3 className="text-blue-400 font-bold text-sm mb-3 pb-2 border-b border-gray-700 whitespace-nowrap">
                                    {column.title}
                                  </h3>
                                )}
                                {column.items.length === 0 ? (
                                  <span className="block px-2 py-2 text-gray-500 text-sm italic">Coming soon</span>
                                ) : (
                                  column.items.map((subItem) => (
                                    <Link
                                      key={subItem.label}
                                      to={subItem.href}
                                      className="block px-2 py-2 text-gray-300 hover:text-white hover:bg-blue-500/20 transition-colors rounded text-sm"
                                      data-testid={`nav-item-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      {subItem.label}
                                    </Link>
                                  ))
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="absolute top-full left-0 w-56 bg-bots-surface border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 mt-1">
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.label}
                              to={subItem.href}
                              className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-blue-500/20 transition-colors first:rounded-t-lg last:rounded-b-lg"
                              data-testid={`nav-item-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={`px-4 py-2 transition-colors font-medium ${item.highlight ? 'text-cyan-400 hover:text-cyan-300 border border-cyan-400/30 rounded-full mx-1' : 'text-white hover:text-blue-400'}`}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              {/* Demo Button */}
              <a
                href="https://demo.aijoomla.org"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:inline-flex px-6 py-2 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-500 transition-colors"
                data-testid="nav-schedule-demo"
              >
                DEMO
              </a>

              {/* Cart Button */}
              {cart_enabled && pawn_checkout && (
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="relative p-2 text-white hover:text-blue-400 transition-colors"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="w-6 h-6" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </button>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-white"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-bots-dark border-t border-gray-800">
            <div className="max-w-7xl mx-auto px-4 py-4">
              {navigation.map((item) => (
                <div key={item.label} className="border-b border-gray-800 last:border-b-0">
                  {(item.items || item.columns) ? (
                    <>
                      <button
                        className="flex items-center justify-between w-full py-3 text-white font-medium"
                        onClick={() => handleDropdownToggle(item.label)}
                      >
                        {item.label}
                        <ChevronDown 
                          className={`w-4 h-4 transition-transform ${
                            activeDropdown === item.label ? 'rotate-180' : ''
                          }`} 
                        />
                      </button>
                      {activeDropdown === item.label && (
                        <div className="pb-3 pl-4 space-y-2">
                          {item.twoColumn ? (
                            item.columns.map((column, idx) => (
                              <div key={idx} className="mb-4">
                                <h3 className="text-blue-400 font-bold text-sm mb-2">
                                  {column.title}
                                </h3>
                                {column.items.map((subItem) => (
                                  <Link
                                    key={subItem.label}
                                    to={subItem.href}
                                    className="block py-2 text-gray-400 hover:text-white transition-colors"
                                  >
                                    {subItem.label}
                                  </Link>
                                ))}
                              </div>
                            ))
                          ) : (
                            item.items.map((subItem) => (
                              <Link
                                key={subItem.label}
                                to={subItem.href}
                                className="block py-2 text-gray-400 hover:text-white transition-colors"
                              >
                                {subItem.label}
                              </Link>
                            ))
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className="block py-3 text-white font-medium"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile CTA Buttons */}
              <div className="mt-4 space-y-3">
                <a
                  href="https://demo.aijoomla.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 bg-blue-600 text-white font-semibold rounded-full text-center hover:bg-blue-500 transition-colors"
                >
                  DEMO
                </a>
              </div>

              {/* Mobile secondary links (moved from hidden top bar) */}
              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-gray-800 pt-4 text-sm">
                {cart_enabled && (
                  <Link to="/shop" className="text-gray-300 hover:text-white" data-testid="mobile-shop-link">Shop</Link>
                )}
                <Link to="/contact" className="text-gray-300 hover:text-white" data-testid="mobile-support-link">Support</Link>
                <Link to="/login" className="text-gray-300 hover:text-white" data-testid="mobile-login-link">Login</Link>
                <a href="mailto:info@aijoomla.org" className="flex items-center gap-1 text-gray-300 hover:text-white" data-testid="mobile-email-link"><Mail className="w-3.5 h-3.5" /> info@aijoomla.org</a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Cart Drawer */}
      <CartDrawer />
    </>
  );
};

export default Header;
